import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: StoreProductQtyBreakPricingStyles;
}

export interface StoreProductQtyBreakPricingStyles {
    breakpriceWrapper?: InjectableCss;
    lineWrapper?: InjectableCss;
    breakPriceSmallText?: TypographyPresentationProps;
    breakPriceBiggerText?: TypographyPresentationProps;
    breakPriceMiddleText?: TypographyPresentationProps;
    breakPriceRightText?: TypographyPresentationProps;
}

const breakPriceTextStyle = {
    color: COLORS.text.dark,
    weight: TYPOGRAPHY.weight.medium,
    lineHeight: 1,
};

export const storeProductQtyBreakPricingStyles: StoreProductQtyBreakPricingStyles = {
    breakpriceWrapper: {
        css: css`
            width: 100%;
        `,
    },
    lineWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 5px;

            &:last-child {
                padding-bottom: 0;
            }
        `,
    },
    breakPriceSmallText: {
        ...breakPriceTextStyle,
        size: TYPOGRAPHY.size.base,
    },
    breakPriceBiggerText: {
        ...breakPriceTextStyle,
        size: TYPOGRAPHY.size.large,
    },
    breakPriceMiddleText: {
        ...breakPriceTextStyle,
        css: css`
            margin-left: 5px;
            padding-right: 5px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: [`${TYPOGRAPHY.size.veryTiny}`, null, `${TYPOGRAPHY.size.tiny}`, null, null],
                            properties: ["font-size"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    breakPriceRightText: {
        ...breakPriceTextStyle,
        size: TYPOGRAPHY.size.large,
        css: css`
            margin-left: auto;
        `,
    },
};

const StoreProductQtyBreakPricing: FC<OwnProps & HasProduct> = ({
    product,
    productInfo: { pricing },
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(storeProductQtyBreakPricingStyles, extendedStyles));

    if (
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2
    ) {
        return null;
    }

    const unitBreakPriceItem = pricing.unitRegularBreakPrices[0];

    return (
        <StyledWrapper {...styles.breakpriceWrapper}>
            {unitBreakPriceItem && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography {...styles.breakPriceSmallText}>{translate("Your unit price:")}</Typography>
                    <Typography {...styles.breakPriceBiggerText}>{unitBreakPriceItem.breakPriceDisplay}</Typography>
                </StyledWrapper>
            )}
            {pricing!.unitRegularBreakPrices!.slice(1).map(breakPrice => (
                <StyledWrapper
                    key={`${breakPrice.breakQty}-${Math.floor(Math.random() * 10000)}`}
                    {...styles.lineWrapper}
                >
                    <Typography {...styles.breakPriceSmallText}>
                        {translate("Your Bulk Price")} ({breakPrice.breakQty}+) :
                    </Typography>
                    <Typography {...styles.breakPriceRightText}>{breakPrice.breakPriceDisplay}</Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default withProduct(StoreProductQtyBreakPricing);
